/* eslint-disable */
import React, { useState, Key, FC, Fragment, useContext } from "react";
import "antd/dist/antd.css";
import { Table, Space, Button } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TableRowSelection } from "antd/es/table/interface";
import "../../../../styles/system.admin.scss";
import SkeletonTable, {
  SkeletonTableColumnsType,
} from "../../../../components/SkeletonTable";
import { CircularProgress, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { SidebarContext } from "../../../auth/ProtectedRoute";

export interface VerifyDataType {
  key: Key;
  id: string;
  organizationName: string;
  userName: string;
  userAvatar: string;
  proofType: string;
  submissionDate: string;
  status: "Approved" | "Pending" | "Requires More Info";
  action?: any;
}

interface VerifyTableProps {
  loading: boolean;
  data: VerifyDataType[];
  onStatusChange: (id: string, status: "Approved" | "Pending" | "Requires More Info") => void;
  onDelete: (id: string) => void;
  currentPage: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

const VerifyTable: FC<VerifyTableProps> = ({
  loading,
  data,
  onStatusChange,
  onDelete,
  currentPage,
  pageSize,
  total,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const { userName, sidebarData } = useContext(SidebarContext);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);

  const handleViewDetails = (id: string) => {
    navigate(`/admin/content-management/pages/verify-page/view/${id}`, {
      state: { userName, sidebar: sidebarData },
    });
  };

  // Switch and case function to dynamically change status text & class
  const statusText = (status: "Approved" | "Pending" | "Requires More Info") => {
    switch (status) {
      case "Approved":
        return (
          <Fragment>
            <div className="group-active-status">Approved</div>
          </Fragment>
        );
      case "Pending":
        return (
          <Fragment>
            <div className="group-inactive-status">Pending</div>
          </Fragment>
        );
      case "Requires More Info":
        return (
          <Fragment>
            <div className="group-inactive-status" style={{ color: "#f59e0b", width: "auto", minWidth: "80px" }}>Requires More Info</div>
          </Fragment>
        );
    }
  };

  // Column definitions matching GroupsTable.tsx structures
  const columns: ColumnsType<VerifyDataType> = [
    {
      title: "Organization Name",
      dataIndex: "organizationName",
      sorter: (a, b) => a.organizationName.localeCompare(b.organizationName),
    },
    {
      title: "Requesting User",
      dataIndex: "userName",
      render: (text, row) => (
        <div className="user-area" style={{ alignItems: "center", display: "flex" }}>
          {row.userAvatar ? (
            <img
              src={row.userAvatar.startsWith("http") ? row.userAvatar : `${process.env.REACT_APP_IMAGE_API_PREFIX || ""}${row.userAvatar}`}
              alt={text}
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                marginRight: "20px",
                objectFit: "cover",
                flexShrink: 0,
              }}
            />
          ) : (
            <div
              style={{
                width: "45px",
                height: "45px",
                borderRadius: "50%",
                backgroundColor: "#4895ef",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                color: "#fff",
                fontWeight: 600,
                fontSize: "16px",
                marginRight: "20px",
                flexShrink: 0,
              }}
            >
              {text?.charAt(0)?.toUpperCase() || "?"}
            </div>
          )}
          <p className="user_name" style={{ margin: 0, fontWeight: 500 }}>
            {text}
          </p>
        </div>
      ),
    },
    {
      title: "Proof Type",
      dataIndex: "proofType",
    },
    {
      title: "Submission Date",
      dataIndex: "submissionDate",
      sorter: (a, b) => new Date(a.submissionDate).getTime() - new Date(b.submissionDate).getTime(),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status: "Approved" | "Pending") => <Fragment>{statusText(status)}</Fragment>,
    },
    {
      title: "Action",
      dataIndex: "action",
      render: (_, row) => (
        <div>
          <button
            className="__btn"
            style={{
              background: "#0d6efd",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "6px 16px",
              fontWeight: 600,
              fontSize: "14px",
              cursor: "pointer",
              transition: "all 0.2s ease-in-out",
              boxShadow: "0 2px 4px rgba(13, 110, 253, 0.2)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleViewDetails(row.id);
            }}
          >
            View
          </button>
        </div>
      ),
    },
  ];

  const onSelectChange = (newSelectedRowKeys: Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection: TableRowSelection<VerifyDataType> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      Table.SELECTION_ALL,
      Table.SELECTION_INVERT,
      Table.SELECTION_NONE,
      {
        key: "odd",
        text: "Select Odd Row",
        onSelect: (changableRowKeys) => {
          let newSelectedRowKeys = [];
          newSelectedRowKeys = changableRowKeys.filter((_, index) => {
            if (index % 2 !== 0) {
              return false;
            }
            return true;
          });
          setSelectedRowKeys(newSelectedRowKeys);
        },
      },
      {
        key: "even",
        text: "Select Even Row",
        onSelect: (changableRowKeys) => {
          let newSelectedRowKeys = [];
          newSelectedRowKeys = changableRowKeys.filter((_, index) => {
            if (index % 2 !== 0) {
              return true;
            }
            return false;
          });
          setSelectedRowKeys(newSelectedRowKeys);
        },
      },
    ],
  };

  return (
    <Fragment>
      <SkeletonTable
        loading={loading}
        columns={columns as SkeletonTableColumnsType[]}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
            <CircularProgress size={40} thickness={4} style={{ color: "#4895ef" }} />
          </Box>
        ) : (
          <Table
            rowSelection={rowSelection}
            columns={columns}
            dataSource={data}
            pagination={{
              showSizeChanger: false,
              current: currentPage,
              pageSize: pageSize,
              total: total,
              onChange: (page) => {
                onPageChange(page);
              },
              showTotal: (showTotal, range) =>
                `${range[0]}-${range[1]} of ${showTotal} items`,
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  handleViewDetails(record.id);
                },
              };
            }}
          />
        )}
      </SkeletonTable>
    </Fragment>
  );
};

export default VerifyTable;
